import React, { useEffect, useRef, useState } from "react";
import {
  getPolicies,
  addPolicy,
  updatePolicy,
  deletePolicy,
  getPolicyCategories,
  addPolicyCategory,
  deletePolicyCategory,
} from "../csrApi";

const CATEGORY_OPTIONS = ["HR-Policy", "CSR Policy", "Annual Return FY 2024-25"];
const isAnnualReturn = (category) => category?.startsWith("Annual Return");

// Backend serves uploaded files from this origin (port 8000 by default).
const MEDIA_BASE = process.env.REACT_APP_API_BASE_URL;
const mediaUrl = (path) => (path?.startsWith("http") ? path : `${MEDIA_BASE}${path}`);

const emptyForm = {
  category: CATEGORY_OPTIONS[0],
  title: "",
  description: "",
  badge: "",
  file: null,
  photo: null,
};

// ---- File validation helpers ----
const ALLOWED_PDF_TYPES = ["application/pdf"];
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_FILE_SIZE_MB = 20; // keep in sync with backend's multer limits (20MB)

const validateFile = (file, allowedTypes, kindLabel) => {
  if (!file) return null;
  if (!allowedTypes.includes(file.type)) {
    return `This file type is not supported. Please upload a ${kindLabel} file (${
      kindLabel === "PDF" ? ".pdf" : ".jpg, .jpeg, .png or .webp"
    }).`;
  }
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return `File is too large. Please upload a ${kindLabel} under ${MAX_FILE_SIZE_MB}MB.`;
  }
  return null;
};

/* ── Lightweight image lightbox, matching the one used on the public
   CSR / Policies pages — click a photo to see it full-size. ── */
const ImageLightbox = ({ src, onClose }) => {
  if (!src) return null;
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-4 -right-4 text-white text-3xl font-bold"
        >
          ×
        </button>
        <img src={src} alt="Preview" className="max-w-[90vw] max-h-[90vh] object-contain" />
      </div>
    </div>
  );
};

const AdminPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Custom categories added via "+ Add new category...". These are now
  // persisted on the server (see /api/policy-categories in index.js)
  // instead of localStorage, so they show up for every admin/browser and
  // survive a refresh instead of only looking "added" temporarily.
  const [customCategories, setCustomCategories] = useState([]); // [{ id, name }]
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);
  const [managingCategories, setManagingCategories] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);

  // Errors shown under each file input
  const [fileError, setFileError] = useState("");
  const [photoError, setPhotoError] = useState("");
  const [formError, setFormError] = useState("");

  // Brief success confirmation ("Saved successfully!" / "Updated successfully!")
  const [toast, setToast] = useState("");
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  // Bumped every time we need the native <input type="file"> to visually
  // reset (switching between records, cancelling, or after a successful
  // save). Changing the `key` forces React to remount the input, which is
  // the only reliable way to clear a file input's displayed filename.
  const [fileInputVersion, setFileInputVersion] = useState(0);

  // Click a thumbnail (list row or the "current photo" preview while
  // editing) to see it full-size, same as the Committee/CSR pages.
  const [lightboxImage, setLightboxImage] = useState(null);

  const formRef = useRef(null);

  const loadPolicies = () => getPolicies().then((res) => setPolicies(res.data));
  const loadCategories = () =>
    getPolicyCategories().then((res) => setCustomCategories(res.data));

  useEffect(() => {
    loadPolicies();
    loadCategories();
  }, []);

  // All category names selectable in the dropdown: built-in defaults, plus
  // any custom ones added so far.
  const selectableCategories = Array.from(
    new Set([...CATEGORY_OPTIONS, ...customCategories.map((c) => c.name)])
  );

  const handleCategorySelect = (e) => {
    const value = e.target.value;
    if (value === "__add_new__") {
      setAddingCategory(true);
      setNewCategoryName("");
      setCategoryError("");
      return;
    }
    setForm((f) => ({ ...f, category: value }));
  };

  const saveNewCategory = async () => {
    const trimmed = newCategoryName.trim();
    setCategoryError("");

    if (!trimmed) {
      setCategoryError("Please enter a category name.");
      return;
    }
    if (selectableCategories.some((c) => c.toLowerCase() === trimmed.toLowerCase())) {
      setCategoryError("This category already exists.");
      return;
    }

    try {
      setSavingCategory(true);
      const res = await addPolicyCategory(trimmed);
      setCustomCategories((prev) => [...prev, res.data.category]);
      setForm((f) => ({ ...f, category: trimmed }));
      setAddingCategory(false);
      setNewCategoryName("");
    } catch (err) {
      setCategoryError(
        err?.response?.data?.error || "Couldn't save this category. Please try again."
      );
    } finally {
      setSavingCategory(false);
    }
  };

  const cancelNewCategory = () => {
    setAddingCategory(false);
    setNewCategoryName("");
    setCategoryError("");
  };

  // Deletes a custom category from the server list. The backend refuses
  // (with a clear error) if any saved document still uses that category,
  // so this can never silently orphan existing Policies/Annual Return docs.
  const removeCategory = async (cat) => {
    if (!window.confirm(`Delete the "${cat.name}" category?`)) return;
    try {
      setDeletingCategoryId(cat.id);
      await deletePolicyCategory(cat.id);
      setCustomCategories((prev) => prev.filter((c) => c.id !== cat.id));
      if (form.category === cat.name) {
        setForm((f) => ({ ...f, category: CATEGORY_OPTIONS[0] }));
      }
    } catch (err) {
      alert(
        err?.response?.data?.error || "Couldn't delete this category. Please try again."
      );
    } finally {
      setDeletingCategoryId(null);
    }
  };

  const resetFileInputs = () => setFileInputVersion((v) => v + 1);

  const scrollToForm = () => {
    // Use a tiny timeout so the state update (which may change form layout,
    // e.g. showing the photo field) finishes before we measure position.
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0] || null;
    const err = validateFile(selected, ALLOWED_PDF_TYPES, "PDF");
    if (err) {
      setFileError(err);
      e.target.value = ""; // clear the invalid selection from the input
      setForm((f) => ({ ...f, file: null }));
      return;
    }
    setFileError("");
    setForm((f) => ({ ...f, file: selected }));
  };

  const handlePhotoChange = (e) => {
    const selected = e.target.files[0] || null;
    const err = validateFile(selected, ALLOWED_IMAGE_TYPES, "image");
    if (err) {
      setPhotoError(err);
      e.target.value = "";
      setForm((f) => ({ ...f, photo: null }));
      return;
    }
    setPhotoError("");
    setForm((f) => ({ ...f, photo: selected }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.category || !form.title || (!form.file && !form.photo && !editingId)) {
      setFormError("Category, title, and at least a PDF or a photo are required.");
      return;
    }

    const fd = new FormData();
    fd.append("category", form.category);
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("badge", form.badge);
    if (form.file) fd.append("file", form.file);
    if (form.photo) fd.append("photo", form.photo);

    try {
      setSaving(true);
      if (editingId) {
        await updatePolicy(editingId, fd);
        showToast("Document updated successfully!");
      } else {
        await addPolicy(fd);
        showToast("Document saved successfully!");
      }
      setForm(emptyForm);
      setEditingId(null);
      setFileError("");
      setPhotoError("");
      resetFileInputs();
      await loadPolicies();
    } catch (err) {
      const serverMessage = err?.response?.data?.message || err?.response?.data?.error;
      setFormError(
        serverMessage ||
          "We couldn't save this document. Please check the file and try again, or contact support if the problem continues."
      );
    } finally {
      setSaving(false);
    }
  };

  const edit = (p) => {
    setEditingId(p.id);
    setAddingCategory(false);
    setForm({
      category: p.category,
      title: p.title,
      description: p.description || "",
      badge: p.badge || "",
      file: null,
      photo: null,
    });
    setFileError("");
    setPhotoError("");
    setFormError("");
    resetFileInputs(); // clear any leftover filename from a previously selected record
    scrollToForm();
  };

  const cancelEdit = () => {
    setEditingId(null);
    setAddingCategory(false);
    setForm(emptyForm);
    setFileError("");
    setPhotoError("");
    setFormError("");
    resetFileInputs();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await deletePolicy(id);
    await loadPolicies();
  };

  const showPhotoField =
    isAnnualReturn(form.category) || !CATEGORY_OPTIONS.includes(form.category);
  const currentlyEditing = policies.find((p) => p.id === editingId);

  // Include the currently selected/edited category even if it isn't in the
  // built-in or custom lists yet (e.g. older data), so the dropdown always
  // has a matching option.
  const dropdownCategories = form.category
    ? Array.from(new Set([...selectableCategories, form.category]))
    : selectableCategories;

  return (
    <div className="max-w-5xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Policies &amp; Annual Returns</h1>

      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-2 rounded shadow-lg text-sm font-medium">
          ✓ {toast}
        </div>
      )}

      <ImageLightbox src={lightboxImage} onClose={() => setLightboxImage(null)} />

      <form
        ref={formRef}
        onSubmit={submit}
        className="bg-white rounded shadow p-4 mb-8 grid gap-3 sm:grid-cols-2"
      >
        <h2 className="sm:col-span-2 font-semibold text-lg">
          {editingId ? "Edit Document" : "Add Document"}
        </h2>

        {formError && (
          <div className="sm:col-span-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded p-2">
            {formError}
          </div>
        )}

        {!addingCategory ? (
          <div>
            <select
              value={form.category}
              onChange={handleCategorySelect}
              className="border rounded p-2 w-full"
            >
              {dropdownCategories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
              <option value="__add_new__">+ Add new category...</option>
            </select>

            {customCategories.length > 0 && (
              <button
                type="button"
                onClick={() => setManagingCategories((v) => !v)}
                className="text-xs text-blue-600 hover:underline mt-1"
              >
                {managingCategories ? "Hide" : "Manage"} custom categories
              </button>
            )}

            {/* Delete option for custom categories. Built-in defaults
                (HR-Policy, CSR Policy, Annual Return FY 2024-25) can't be
                removed here — only categories added via "+ Add new
                category..." can. */}
            {managingCategories && (
              <div className="mt-2 border rounded p-2 bg-gray-50">
                {customCategories.length === 0 ? (
                  <p className="text-xs text-gray-500">No custom categories yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {customCategories.map((c) => (
                      <li
                        key={c.id}
                        className="flex items-center justify-between text-sm bg-white border rounded px-2 py-1"
                      >
                        <span>{c.name}</span>
                        <button
                          type="button"
                          onClick={() => removeCategory(c)}
                          disabled={deletingCategoryId === c.id}
                          className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          {deletingCategoryId === c.id ? "Deleting..." : "Delete"}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="border border-blue-200 bg-blue-50 rounded p-3">
            <label className="block text-xs text-gray-600 mb-1">New category name</label>
            <div className="flex flex-wrap gap-2 items-start">
              <input
                type="text"
                placeholder="e.g. Annual Return FY 2025-26"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveNewCategory();
                  }
                }}
                className="border rounded p-2 flex-1 min-w-[160px]"
                autoFocus
              />
              <button
                type="button"
                onClick={saveNewCategory}
                disabled={savingCategory}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm disabled:opacity-60"
              >
                {savingCategory ? "Saving..." : "Save & Use"}
              </button>
              <button
                type="button"
                onClick={cancelNewCategory}
                disabled={savingCategory}
                className="bg-gray-300 hover:bg-gray-400 px-3 py-2 rounded text-sm disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
            {categoryError && (
              <p className="text-xs text-red-600 mt-1">{categoryError}</p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              This is saved to the server right away and added to the dropdown above for
              every admin.
            </p>
          </div>
        )}

        <input
          type="text"
          placeholder="Title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="border rounded p-2"
        />

        <input
          type="text"
          placeholder="Badge (optional, e.g. 'New')"
          value={form.badge}
          onChange={(e) => setForm({ ...form, badge: e.target.value })}
          className="border rounded p-2"
        />

        <div>
          <label className="block text-xs text-gray-500 mb-1">PDF file</label>

          {/* Show what's currently attached when editing, since a native
              file input can never be pre-filled with the existing file. */}
          {editingId && currentlyEditing?.fileUrl && !form.file && (
            <a
              href={mediaUrl(currentlyEditing.fileUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-xs text-blue-600 underline mb-1"
            >
              Current file: view existing PDF (choose a new file below to replace it)
            </a>
          )}

          <input
            key={`policy-file-${fileInputVersion}`}
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className="border rounded p-2 w-full"
          />
          {fileError && <p className="text-xs text-red-600 mt-1">{fileError}</p>}
        </div>

        {showPhotoField && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Photo (optional — shown on the Annual Return tab)
            </label>

            {editingId && currentlyEditing?.photo && !form.photo && (
              <div className="flex items-center gap-2 mb-1">
                <img
                  src={mediaUrl(currentlyEditing.photo)}
                  alt="Current"
                  onClick={() => setLightboxImage(mediaUrl(currentlyEditing.photo))}
                  className="w-10 h-10 object-cover rounded border cursor-pointer hover:opacity-80"
                />
                <span className="text-xs text-gray-500">
                  Current photo (click to enlarge, or choose a new file below to replace it)
                </span>
              </div>
            )}

            <input
              key={`policy-photo-${fileInputVersion}`}
              type="file"
              accept="image/png, image/jpeg, image/webp"
              onChange={handlePhotoChange}
              className="border rounded p-2 w-full"
            />
            {photoError && <p className="text-xs text-red-600 mt-1">{photoError}</p>}
          </div>
        )}

        <textarea
          placeholder="Description (optional)"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="border rounded p-2 sm:col-span-2"
          rows={2}
        />

        <div className="sm:col-span-2 flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-60 flex items-center gap-2"
          >
            {saving && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}
            {saving ? "Saving..." : editingId ? "Update" : "Add"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded disabled:opacity-60"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="grid gap-4">
        {policies.map((p) => (
          <div
            key={p.id}
            className="flex items-center justify-between bg-white rounded shadow p-4"
          >
            <div className="flex items-center gap-4">
              {p.photo && (
                <img
                  src={mediaUrl(p.photo)}
                  alt={p.title}
                  onClick={() => setLightboxImage(mediaUrl(p.photo))}
                  className="w-14 h-14 object-cover rounded border flex-shrink-0 cursor-pointer hover:opacity-80"
                />
              )}
              <div>
                <p className="text-xs text-orange-600 font-semibold">{p.category}</p>
                <p className="font-semibold">
                  {p.title}{" "}
                  {p.badge && (
                    <span className="ml-2 text-xs bg-orange-200 text-gray-800 px-2 py-0.5 rounded-full align-middle">
                      {p.badge}
                    </span>
                  )}
                </p>
                {p.description && (
                  <p className="text-sm text-gray-500">{p.description}</p>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {p.fileUrl && (
                <a
                  href={mediaUrl(p.fileUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                >
                  Open PDF
                </a>
              )}
              <button
                onClick={() => edit(p)}
                className="text-sm bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded"
              >
                Edit
              </button>
              <button
                onClick={() => remove(p.id)}
                className="text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {policies.length === 0 && (
          <p className="text-center text-gray-400">No documents added yet.</p>
        )}
      </div>
    </div>
  );
};

export default AdminPolicies;